import {
    APP_INITIALIZER,
    NgModule,
    CUSTOM_ELEMENTS_SCHEMA,
    LOCALE_ID,
    ErrorHandler,
} from '@angular/core';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { AppComponent } from '@app/app.component';
import { AppRoutingModule } from '@app/app-routing.module';
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withFetch,
    withInterceptorsFromDi,
} from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import {
    API_DOC_LANGS_MAP,
    ConfigurationService,
    DISABLE_NOTIFICATIONS_ROLES,
    ROLE,
    ROLE_ACCOUNT_TYPE,
} from '@app/configuration.service';

import { registerLocaleData } from '@angular/common';
import localeRu from '@angular/common/locales/ru';
import localeEn from '@angular/common/locales/en';
import { ServiceWorkerModule } from '@angular/service-worker';
import { BazisHeaderInterceptor } from '@bazis/interceptors/header.interceptor';
import { BazisAuthInterceptor } from '@bazis/interceptors/auth.interceptor';
import { BazisHttpErrorsInterceptor } from '@bazis/interceptors/http-errors.interceptor';
import { BazisSignInterceptor } from '@bazis/interceptors/sign.interceptor';

import { SharedModule } from '@shared/shared.module';
import { BazisConfirmOrgInfoInterceptor } from '@bazis/interceptors/confirm-org-info.interceptor';
import { BazisLocaleService } from '@bazis/shared/services/locale.service';
import { LocalDatePipe } from '@bazis/shared/pipes/local-date.pipe';
import * as Sentry from '@sentry/angular';
import { isLocal } from '@bazis/utils';
import { BazisAuthService } from '@bazis/shared/services/auth.service';
import { LgAuthService } from '@shared/services/auth.service';
import { NgxMaskDirective, NgxMaskPipe, provideEnvironmentNgxMask } from 'ngx-mask';
import { TranslocoRootModule } from '@shared/modules/transloco-root.module';
import { BazisConfigurationService } from '@bazis/configuration.service';
import { DISABLE_NOTIFICATIONS_ROLES_DATA } from '@bazis/shared/services/notification.service';
import { BAZIS_API_V } from '@bazis/shared/services/srv.service';
import { ModalsComponent } from '@bazis/shared/components/modal/modals.component';
import { AlertComponent } from '@shared/components/alert/alert.component';
import { ContextMenusComponent } from '@bazis/shared/components/context-menu/context-menus.component';
import { ToastComponent } from '@bazis/shared/components/toast/toast.component';
import { GlobalLoaderComponent } from '@bazis/shared/components/loaders/global-loader.component';
import { PluralPipe } from '@bazis/shared/pipes/plural.pipe';
import { WS_PING_PONG_SUPPORT } from '@bazis/shared/services/web-socket.service';
import { DocumentService } from '@shared/services/document.service';
import { BazisDocumentService } from '@bazis/shared/services/document.service';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { FaqService } from '@shared/services/faq.service';
import { BazisFaqService } from '@bazis/shared/services/faq.service';
import { API_DOC_LANGS } from '@bazis/shared/tokens/api_doc_langs.token';
registerLocaleData(localeRu, 'ru');
registerLocaleData(localeEn, 'en');

export function loadConfiguration(configService: ConfigurationService) {
    return () => configService.loadConfiguration();
}

const interceptorProviders = [
    {
        provide: HTTP_INTERCEPTORS,
        useClass: BazisHeaderInterceptor,
        multi: true,
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: BazisConfirmOrgInfoInterceptor,
        multi: true,
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: BazisSignInterceptor,
        multi: true,
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: BazisAuthInterceptor,
        multi: true,
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: BazisHttpErrorsInterceptor,
        multi: true,
    },
];
const sentryProviders = [];
if (!isLocal()) {
    sentryProviders.push({
        provide: ErrorHandler,
        useValue: Sentry.createErrorHandler({
            showDialog: false,
            logErrors: true,
        }),
    });
}

@NgModule({
    declarations: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        AppRoutingModule,
        FormsModule,
        BrowserAnimationsModule,
        SharedModule,
        TranslocoRootModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: !isLocal(),
            registrationStrategy: 'registerWhenStable:30000',
            //registrationStrategy: 'registerImmediately',
        }),
        NgxMaskDirective,
        NgxMaskPipe,
        ModalsComponent,
        AlertComponent,
        ContextMenusComponent,
        ToastComponent,
        GlobalLoaderComponent,
    ],
    providers: [
        { provide: BazisAuthService, useExisting: LgAuthService },
        { provide: BazisDocumentService, useExisting: DocumentService },
        { provide: BazisFaqService, useExisting: FaqService },
        { provide: BazisConfigurationService, useExisting: ConfigurationService },
        {
            provide: APP_INITIALIZER,
            useFactory: loadConfiguration,
            deps: [ConfigurationService],
            multi: true,
        },
        {
            provide: BAZIS_API_V,
            useValue: '2',
        },
        { provide: WS_PING_PONG_SUPPORT, useValue: true },
        Title,
        {
            provide: LOCALE_ID,
            deps: [BazisLocaleService],
            useFactory: (localeService) => localeService.locale,
        },
        ...interceptorProviders,
        ...sentryProviders,
        LocalDatePipe,
        provideEnvironmentNgxMask(),
        { provide: DISABLE_NOTIFICATIONS_ROLES_DATA, useValue: DISABLE_NOTIFICATIONS_ROLES },
        { provide: 'ROLE', useValue: ROLE },
        { provide: 'ROLE_ACCOUNT_TYPE', useValue: ROLE_ACCOUNT_TYPE },
        { provide: API_DOC_LANGS, useValue: API_DOC_LANGS_MAP },
        provideHttpClient(withInterceptorsFromDi()),
        provideHttpClient(withFetch()),
        PluralPipe,
        {
            provide: TRANSLOCO_SCOPE,
            useValue: ['onboarding'],
        },
    ],
})
export class AppModule {}
